import {
  AfterViewInit, ChangeDetectorRef, Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, Renderer2,
  TemplateRef
} from '@angular/core';
import { PinnedHeaderMirror } from '../utils/pinned-header-mirror';

/** A split table's group under the pinned header: its row, and the label pinned under the header for it. */
interface PinnedGroup {
  row: HTMLElement | null;
  label: string | null;
  list: string | null;
}

const NO_GROUP: PinnedGroup = { row: null, label: null, list: null };

/**
 * Pins the title band and column header row of the table being scrolled through under the app
 * header (RA-2838), for pages that stack several `.sticky-table` cards. Phones only by default;
 * `pinMedia` widens it, e.g. to every width on Portfolio (RA-2879).
 *
 * Each table's `.sticky-table` is its own `overflow: auto` box, so CSS `position: sticky` can't
 * pin anything in it against the page. Instead the page renders one fixed host carrying this
 * directive, and each card registers itself with `rmsPinnedTableSource`. The page renders the
 * active card's band and header row into the host from the SAME `ng-template`s the card renders,
 * so the pinned copy is live: taps sort, carets and counts update. A `cloneNode` copy loses its
 * handlers and goes stale.
 *
 * This directive only does geometry:
 * - which card is active: the last one whose table has reached the copy's header row, i.e. the pin
 *   line plus the band the copy renders, and hasn't scrolled past it. At that moment the copy's
 *   header lands exactly over the real one, so the first pin and every handoff are seamless, even
 *   where the copy's band is slimmer than the card's own title row (Portfolio, RA-2879).
 * - the push-up offset: as the active table's bottom rises, the stack slides up with it.
 * - column widths and sideways position, through `PinnedHeaderMirror`: the copy's header row
 *   sits in its own scrolling viewport kept in step with its table, so a sticky SYM column keeps
 *   working in both.
 * - for a table split into groups (`pinGroupRows`), which group the pinned header belongs to: the
 *   last whose row has reached the pinned group row's place, just under the copy's header rows. Its
 *   label (`activeGroupLabel`, so the page pins the row there) and list (`activeGroupList`, so the
 *   page points the copy at that group's sort) switch together, as soon as the next group's row
 *   reaches that place (RA-2879 QA). Anything the copy pins under the group row, e.g. a "Sorted
 *   by" line, sits under the group row in the table too, so the copy lines up with the table.
 *
 * The pin line is read off the host's own box (its `top` is the stylesheet's), so the app
 * header's height isn't mirrored as a constant here. Scrolling runs outside the Angular zone;
 * change detection only runs when the active card changes.
 */
@Directive({
  selector: '[rmsPinnedTableStack]',
  exportAs: 'rmsPinnedTableStack',
  standalone: false
})
export class PinnedTableStackDirective implements AfterViewInit, OnDestroy {
  /** Phones only. Matches the tables' own `media-breakpoint-down(md)`; at 768 Movers goes two-up. */
  public static readonly PHONE_QUERY = '(max-width: 767.98px)';
  /**
   * Slack for a group's rows lined up with the copy's: a header this close above counts as under the copy's, and a
   * row this close below the stack still shows, since the real and pinned rows can differ by a fraction of a pixel.
   */
  private static readonly TOLERANCE = 1;

  /** The media query the stack pins under, read once at start-up; `all` pins at every width. */
  @Input() pinMedia = PinnedTableStackDirective.PHONE_QUERY;

  /** The card whose band and header are pinned, or null when nothing is. Read by the page template. */
  public active: PinnedTableSourceDirective | null = null;
  /** The text of the active group's row, pinned under the header; null outside a split table. */
  public activeGroupLabel: string | null = null;
  /** The active group's `data-list`, e.g. the key of the list it sorts by. */
  public activeGroupList: string | null = null;

  private readonly sources = new Set<PinnedTableSourceDirective>();
  private mediaQuery: MediaQueryList | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private mirror: PinnedHeaderMirror | null = null;
  /** The table last bound, kept even when the stack has no viewport to mirror it into, so it isn't retried each frame. */
  private boundTable: HTMLElement | null = null;
  private readonly pageListeners: Array<() => void> = [];
  private frame: number | null = null;
  private needsLayout = true;
  /** The translateY currently applied, so the pin line can be read back off the moved host. */
  private offset = 0;
  private viewInit = false;
  /** The real row of the active group, for scrolling back to it. */
  private activeGroupRow: HTMLElement | null = null;
  /** What the stack pinned under the group row (e.g. a "Sorted by" line) when the active group took over. */
  private groupHold = 0;
  /** The height of the band each card's copy rendered, measured the first time it pins. */
  private readonly bandHeights = new WeakMap<PinnedTableSourceDirective, number>();
  /** The last band measured on any card, as the estimate for a card that hasn't pinned yet. */
  private lastBandHeight: number | null = null;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    this.viewInit = true;
    this.mediaQuery = window.matchMedia?.(this.pinMedia) ?? null;
    const host = this.elementRef.nativeElement;

    this.ngZone.runOutsideAngular(() => {
      const onScroll = () => this.scheduleUpdate(false);
      const onResize = () => this.scheduleUpdate();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResize);
      this.mediaQuery?.addEventListener?.('change', onResize);
      this.pageListeners.push(
        () => window.removeEventListener('scroll', onScroll),
        () => window.removeEventListener('resize', onResize),
        () => this.mediaQuery?.removeEventListener?.('change', onResize)
      );

      // The page moves without a scroll event when a card above grows or shrinks (Stock Halts,
      // a refresh, an expanded row, an appended page of rows). The cards and the page container
      // cover those; the host covers the stack's own height changing, e.g. a sort line appearing.
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(host);
        if (host.parentElement) {
          this.resizeObserver.observe(host.parentElement);
        }
        this.sources.forEach(source => this.resizeObserver.observe(source.element));
      }
    });

    this.scheduleUpdate();
  }

  ngOnDestroy(): void {
    this.pageListeners.forEach(remove => remove());
    this.unbindMirror();
    this.resizeObserver?.disconnect();
    if (this.frame !== null) {
      window.cancelAnimationFrame(this.frame);
    }
  }

  /** Adds a card to the set that can pin. Called by `rmsPinnedTableSource`. */
  public register(source: PinnedTableSourceDirective): void {
    this.sources.add(source);
    this.resizeObserver?.observe(source.element);
    this.scheduleUpdate();
  }

  /**
   * Removes a card, e.g. one taken out by `*ngIf`. This runs during change detection, so a pin it
   * held is dropped on the next frame rather than re-rendered from here.
   */
  public unregister(source: PinnedTableSourceDirective): void {
    this.sources.delete(source);
    this.resizeObserver?.unobserve(source.element);
    this.scheduleUpdate();
  }

  /**
   * Queues a recompute for the next frame, coalescing every trigger in between.
   * @param layout: also re-measure column widths and the host's box. A plain vertical scroll
   *   only needs the active card and the offset.
   */
  public scheduleUpdate(layout = true): void {
    this.needsLayout = this.needsLayout || layout;
    if (!this.viewInit || this.frame !== null) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    });
  }

  /**
   * Scrolls the page so the active table's header sits exactly under the copy of it, which leaves
   * the table's first row just under the stack. For a sort or Reset tapped in the copy: the reader
   * is deep in a table whose rows have just reordered. Waits a frame so the re-sorted rows and any
   * change to the pinned line have rendered.
   *
   * In a split table it brings back the current group instead: the group's row goes exactly under
   * the pinned copy of it, with anything pinned under that (a "Sorted by" line) over the table's own
   * and the group's first row just under the stack, so the reader stays in the group they sorted.
   */
  public scrollActiveToFirstRow(): void {
    const source = this.active;
    if (!source) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      window.requestAnimationFrame(() => {
        const row = this.activeGroupRow?.isConnected ? this.activeGroupRow : null;
        let delta = 0;
        if (row) {
          // Rounded up, so the table's header ends on or just above the copy's and the card stays pinned.
          delta = Math.ceil(row.getBoundingClientRect().top - this.headerBottom(source));
        } else {
          // Rounded up, as above.
          const top = source.scrollElement?.getBoundingClientRect().top;
          delta = top === undefined ? 0 : Math.ceil(top - this.pinLine() - this.bandHeight(source));
        }
        if (delta !== 0) {
          window.scrollBy(0, delta);
        }
      });
    });
  }

  /** Picks the active card and its group, then places and sizes the stack over it. */
  private update(): void {
    this.render(this.mediaQuery?.matches ? this.findActive() : null);
    // A card's band height is only known once its copy has rendered. If it differs from what the
    // card was picked with, e.g. on its first pin, pick again: still inside this frame, so a pin
    // that came too early is dropped before it paints. A second pass covers a re-pick landing on
    // another card that hasn't pinned yet, whose band is still an estimate.
    for (let pass = 0; pass < 2 && this.active && this.measureBand(this.active); pass++) {
      this.render(this.findActive());
    }
    if (!this.active) {
      this.clearPosition();
      return;
    }
    // A card can swap its table while pinned, e.g. Portfolio's Long card on a view or split change;
    // the old one is detached and measures zero, so mirror the new one.
    const table = this.active.scrollElement;
    if (table && table !== this.boundTable) {
      this.bindMirror(table);
      this.needsLayout = true;
    }
    if (this.needsLayout) {
      this.needsLayout = false;
      this.layout(this.active);
    }
    this.position(this.active);
  }

  /** Renders a card and its group in the stack, unless they are already the ones shown. */
  private render(next: PinnedTableSourceDirective | null): void {
    const group = this.findGroup(next);
    this.activeGroupRow = group.row;
    if (next !== this.active || group.label !== this.activeGroupLabel || group.list !== this.activeGroupList) {
      this.setActive(next, group);
    }
  }

  /**
   * The last card whose table has reached the copy's header row and still reaches below the pin
   * line. Before a card has pinned once its band height is unknown, so it counts from the earlier
   * of its own top and another card's band (a card with no title of its own, like Portfolio's Fully
   * Boxed on phones, is due before its top arrives); `update()` then checks that against the band
   * it renders and drops a pin that came early.
   */
  private findActive(): PinnedTableSourceDirective | null {
    const pinTop = this.pinLine();
    let found: PinnedTableSourceDirective | null = null;
    this.sources.forEach(source => {
      const table = source.scrollElement;
      if (!source.pinEnabled || !table) {
        return;
      }
      const box = table.getBoundingClientRect();
      const reached = this.bandHeights.has(source)
        ? box.top <= pinTop + this.bandHeight(source)
        : source.element.getBoundingClientRect().top <= pinTop
          || (this.lastBandHeight !== null && box.top <= pinTop + this.lastBandHeight);
      if (reached && box.bottom > pinTop) {
        found = source;
      }
    });
    return found;
  }

  /** The height of the band a card's copy renders above its header row, 0 until it is known. */
  private bandHeight(source: PinnedTableSourceDirective): number {
    return this.bandHeights.get(source) ?? 0;
  }

  /**
   * Records the band the active card's copy rendered: from the stack's top to its header viewport.
   * @returns whether it changed, so the active card may have to be picked again
   */
  private measureBand(source: PinnedTableSourceDirective): boolean {
    const host = this.elementRef.nativeElement;
    const viewport = host.querySelector('.pinned-table-viewport');
    if (!viewport) {
      return false;
    }
    const height = Math.max(0, viewport.getBoundingClientRect().top - host.getBoundingClientRect().top);
    if (this.bandHeights.get(source) === height) {
      return false;
    }
    this.bandHeights.set(source, height);
    this.lastBandHeight = height;
    return true;
  }

  /**
   * The split table's group the pinned header belongs to: the last whose row has reached the stack's
   * bottom as it shows the group before, i.e. the pinned group row's place under the copy's header
   * rows plus whatever is pinned under that row, e.g. a "Sorted by" line. The group keeps that
   * allowance while it is active, so a switch that drops or adds the line can't switch it back.
   */
  private findGroup(source: PinnedTableSourceDirective | null): PinnedGroup {
    const table = source?.scrollElement;
    if (!source?.pinGroupRows || !table) {
      this.groupHold = 0;
      return NO_GROUP;
    }
    const headerBottom = this.headerBottom(source) + PinnedTableStackDirective.TOLERANCE;
    const underGroup = this.underGroupHeight();
    const rows = Array.from(table.querySelectorAll<HTMLElement>(source.pinGroupRows));
    const current = this.activeGroupRow ? rows.indexOf(this.activeGroupRow) : -1;
    let found = -1;
    rows.forEach((row, i) => {
      let allowance = 0;
      if (i > current) {
        allowance = underGroup;
      } else if (i === current) {
        allowance = this.groupHold;
      }
      const box = row.getBoundingClientRect();
      if (box.top <= headerBottom + box.height + allowance) {
        found = i;
      }
    });
    if (found !== current) {
      this.groupHold = found > current ? underGroup : 0;
    }
    if (found < 0) {
      return NO_GROUP;
    }
    const groupRow = rows[found];
    return { row: groupRow, label: groupRow.textContent.trim(), list: groupRow.dataset['list'] ?? null };
  }

  /** The height the stack renders under the copy's table, e.g. a "Sorted by" line; 0 before it renders. */
  private underGroupHeight(): number {
    const host = this.elementRef.nativeElement;
    const viewport = host.querySelector('.pinned-table-viewport');
    return viewport ? Math.max(0, host.getBoundingClientRect().bottom - viewport.getBoundingClientRect().bottom) : 0;
  }

  /**
   * The bottom of the copy's header rows, where the pinned group row starts: the copy's header top
   * plus the table's own header rows, which the copy mirrors. Read off the table, so it is known
   * before the copy renders.
   */
  private headerBottom(source: PinnedTableSourceDirective): number {
    const head = source.scrollElement?.querySelector(':scope > table > thead');
    return this.pinLine() + this.bandHeight(source) + (head?.getBoundingClientRect().height ?? 0);
  }

  /** The y the stack pins at: the host's own top, less whatever push-up is applied to it. */
  private pinLine(): number {
    return this.elementRef.nativeElement.getBoundingClientRect().top - this.offset;
  }

  /**
   * Swaps the rendered copy, or just its group row. Inside the zone, so the copy's own listeners
   * (sort taps, Reset) are bound where they trigger change detection; rendered synchronously so
   * the copy can be measured and placed in this same frame, before it paints.
   */
  private setActive(source: PinnedTableSourceDirective | null, group: PinnedGroup): void {
    const swapped = source !== this.active;
    if (swapped) {
      this.unbindMirror();
    }
    this.ngZone.run(() => {
      this.active = source;
      this.activeGroupLabel = group.label;
      this.activeGroupList = group.list;
      this.changeDetectorRef.detectChanges();
    });
    this.needsLayout = true;
    const table = source?.scrollElement;
    if (swapped && table) {
      this.bindMirror(table);
    }
  }

  /** Mirrors a table's header onto the copy's viewport, replacing any earlier mirror. */
  private bindMirror(table: HTMLElement): void {
    this.unbindMirror();
    this.boundTable = table;
    const viewport = this.elementRef.nativeElement.querySelector('.pinned-table-viewport') as HTMLElement | null;
    if (viewport) {
      this.mirror = new PinnedHeaderMirror(table, viewport, this.ngZone, () => this.scheduleUpdate());
    }
  }

  /** Sizes the host to the active table's box, then mirrors its columns and sideways offset. */
  private layout(source: PinnedTableSourceDirective): void {
    const box = source.scrollElement?.getBoundingClientRect();
    if (!box || !this.mirror) {
      return;
    }
    // Fractional: offsetWidth rounds, and the copy has to sit exactly over the table.
    const host = this.elementRef.nativeElement;
    this.renderer.setStyle(host, 'left', `${box.left}px`);
    this.renderer.setStyle(host, 'width', `${box.width}px`);
    this.mirror.layout();
  }

  /** Applies the push-up: once the table's bottom rises into the stack, the stack goes with it. */
  private position(source: PinnedTableSourceDirective): void {
    const host = this.elementRef.nativeElement;
    const pinTop = this.pinLine();
    const height = host.getBoundingClientRect().height;
    const bottom = source.scrollElement?.getBoundingClientRect().bottom ?? pinTop;
    const offset = Math.min(0, bottom - pinTop - height);
    if (offset !== this.offset) {
      this.offset = offset;
      this.renderer.setStyle(host, 'transform', offset ? `translateY(${offset}px)` : null);
    }
  }

  private clearPosition(): void {
    const host = this.elementRef.nativeElement;
    this.offset = 0;
    ['transform', 'left', 'width'].forEach(property => this.renderer.removeStyle(host, property));
  }

  private unbindMirror(): void {
    this.mirror?.destroy();
    this.mirror = null;
    this.boundTable = null;
  }
}

/**
 * Marks a table card (its title band followed by its `.sticky-table`) as one that can pin in a
 * page's `rmsPinnedTableStack`, and hands the stack the templates to render the copy from. The
 * card must render the same templates itself, so the copy and the real table can't drift apart.
 */
@Directive({
  selector: '[rmsPinnedTableSource]',
  standalone: false
})
export class PinnedTableSourceDirective implements OnInit, OnChanges, OnDestroy {
  /** The page's stack this card pins in. */
  @Input('rmsPinnedTableSource') stack: PinnedTableStackDirective | null = null;
  /** The title band above the table. */
  @Input() pinBand: TemplateRef<unknown> | null = null;
  /** The header `<tr>`. */
  @Input() pinHead: TemplateRef<unknown> | null = null;
  /** An optional full-width line pinned under the header row, e.g. Movers' "Sorted by". */
  @Input() pinLine: TemplateRef<unknown> | null = null;
  @Input() pinLineVisible = false;
  /** Context the templates are rendered with. */
  @Input() pinContext: unknown = null;
  /** False for a table that must never pin, e.g. one showing its no-content row. */
  @Input() pinEnabled = true;
  /**
   * For a table split into groups: the selector of its group rows (e.g. `tr.sub-title`), so the
   * stack can report the group under the pinned header. Each row's text is its label and its
   * `data-list` names the group, e.g. the list it sorts.
   */
  @Input() pinGroupRows: string | null = null;
  /** The card's sideways-scrolling table box, for cards whose table doesn't sit in a `.sticky-table`. */
  @Input() pinScrollSelector = '.sticky-table';

  /**
   * `pinContext` plus `pinned: true`, for the copy. Templates use it to take the copy's controls
   * out of the tab order, since the copy is hidden from assistive tech, and to tell their handlers
   * the tap came from the copy.
   */
  public copyContext: Record<string, unknown> = { pinned: true };

  constructor(private readonly elementRef: ElementRef<HTMLElement>) { }

  ngOnInit(): void {
    this.stack?.register(this);
  }

  ngOnChanges(): void {
    // Spreading a null context is a no-op, so no fallback is needed.
    this.copyContext = { ...(this.pinContext as Record<string, unknown>), pinned: true };
    this.stack?.scheduleUpdate();
  }

  ngOnDestroy(): void {
    this.stack?.unregister(this);
  }

  public get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  /** The card's horizontally scrolling table box, looked up live since it can sit under `*ngIf`. */
  public get scrollElement(): HTMLElement | null {
    return this.elementRef.nativeElement.querySelector(this.pinScrollSelector);
  }
}
